import React from "react";
import { platformStats } from "../../../data/mockData";
import StatsWidget from "../../creator/components/StatsWidget";
import Card from "../../../components/ui/Card";
import DateRangeFilter from "../components/DateRangeFilter";
import {
  Users,
  Briefcase,
  DollarSign,
  Activity,
  AlertCircle,
  AlertTriangle,
  Info,
  UserPlus,
  Gift,
  CheckCircle,
  ChevronRight
} from "lucide-react";
import { Link } from "react-router-dom";

const AdminDashboardPage = () => {
  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-gradient">Platform Overview</h1>
          <p className="text-slate-500 font-medium mt-1">
            Global heartbeat and system health at a glance.
          </p>
        </div>
        <DateRangeFilter />
      </div>

      {/* KPI HIGHLIGHTS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsWidget
          icon={Users}
          title="Total Community"
          value={platformStats.totalUsers.value}
          color="text-primary"
        />
        <StatsWidget
          icon={Briefcase}
          title="Verified Creators"
          value={platformStats.totalCreators.value}
          color="text-purple-600"
        />
        <StatsWidget
          icon={DollarSign}
          title="Platform Gross"
          value={platformStats.totalRevenue.value}
          color="text-emerald-600"
        />
        <Card className="flex flex-col justify-center items-center p-4 bg-indigo-600 !text-white border-none shadow-xl shadow-indigo-100 group cursor-pointer hover:scale-[1.02] transition-all">
           <Activity size={24} className="mb-2 opacity-80" />
           <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-60">System Health</p>
           <p className="text-2xl font-black">100.0% LIVE</p>
        </Card>
      </div>

      {/* Main Grid: Health vs Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* System Health / Critical Feed */}
        <div className="lg:col-span-1 space-y-6">
           <Card className="bg-slate-800 !text-white border-none shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                 <h3 className="text-lg font-black flex items-center gap-2">
                   <AlertCircle size={20} className="text-orange-400" />
                   System Alerts
                 </h3>
                 <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-[10px] font-black rounded uppercase tracking-tighter">High Priority</span>
              </div>
              
              <div className="space-y-4">
                {platformStats.systemAlerts.map(alert => (
                  <div key={alert.id} className="flex gap-4 p-4 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-colors">
                    <div className={`shrink-0 mt-1 ${
                      alert.type === 'error' ? 'text-red-400' :
                      alert.type === 'warning' ? 'text-orange-400' :
                      'text-blue-400'
                    }`}>
                      {alert.type === 'error' ? <AlertCircle size={20} /> : 
                       alert.type === 'warning' ? <AlertTriangle size={20} /> : 
                       <Info size={20} />}
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-bold leading-tight line-clamp-2 text-white">{alert.message}</p>
                        <p className="text-[10px] text-white/40 mt-1.5 font-black uppercase tracking-widest">{alert.time}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <button className="w-full mt-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-white/40 hover:text-white border-t border-white/10 transition-all pt-6">
                 Full Incident Logs
              </button>
           </Card>

           <Card className="overflow-hidden border-slate-200">
               <h3 className="text-lg font-black text-slate-800 mb-6">Pending Tasks</h3>
               <div className="space-y-4">
                  {[
                    { label: "Identity Verification", count: 18, color: "bg-blue-500" },
                    { label: "Content Reports", count: 34, color: "bg-orange-500" },
                    { label: "Payout Requests", count: 12, color: "bg-emerald-500" }
                  ].map((task, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer group">
                       <span className="text-sm font-bold text-slate-600">{task.label}</span>
                       <div className="flex items-center gap-2">
                          <span className={`${task.color} text-white px-2 py-0.5 rounded-lg text-xs font-black`}>{task.count}</span>
                          <ChevronRight size={14} className="text-slate-300 group-hover:text-slate-900 transition-colors" />
                       </div>
                    </div>
                  ))}
               </div>
           </Card>
        </div>

        {/* Highlight Stats Panels */}
        <div className="lg:col-span-2 space-y-8">
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Creator Growth Preview */}
              <Card className="flex flex-col">
                  <div className="flex items-center justify-between mb-8">
                    <h3 className="text-lg font-black text-slate-800">Growth Flow</h3>
                    <Link to="/admin/analytics" className="text-[10px] font-black text-primary uppercase tracking-widest border-b-2 border-primary/20 pb-0.5">Report</Link>
                  </div>
                  <div className="flex-1 flex flex-col justify-center gap-6">
                      <div>
                         <div className="flex justify-between text-[11px] font-black uppercase text-slate-500 mb-2">
                            <span>Active Creators</span>
                            <span className="text-slate-900">82%</span>
                         </div>
                         <div className="h-4 w-full bg-slate-100 rounded-2xl overflow-hidden shadow-inner p-1">
                            <div className="h-full bg-emerald-500 rounded-2xl shadow-lg shadow-emerald-200" style={{width: '82%'}}></div>
                         </div>
                      </div>
                      <div>
                         <div className="flex justify-between text-[11px] font-black uppercase text-slate-500 mb-2">
                            <span>Approval Queue</span>
                            <span className="text-slate-900">18%</span>
                         </div>
                         <div className="h-4 w-full bg-slate-100 rounded-2xl overflow-hidden shadow-inner p-1">
                            <div className="h-full bg-orange-400 rounded-2xl shadow-lg shadow-orange-200" style={{width: '18%'}}></div>
                         </div>
                      </div>
                  </div>
                  <div className="mt-10 pt-6 border-t border-slate-50 flex items-center justify-between">
                     <div>
                        <p className="text-2xl font-black text-slate-900">+1,450</p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">New Applications (MOM)</p>
                     </div>
                     <div className="w-12 h-12 bg-primary/5 rounded-2xl flex items-center justify-center text-primary">
                        <UserPlus size={24} />
                     </div>
                  </div>
              </Card>

              {/* Revenue Snapshot */}
              <Card className="flex flex-col bg-slate-50/50">
                  <div className="flex items-center justify-between mb-8">
                    <h3 className="text-lg font-black text-slate-800">Daily Revenue</h3>
                    <Link to="/admin/analytics" className="text-[10px] font-black text-primary uppercase tracking-widest border-b-2 border-primary/20 pb-0.5">Real-time</Link>
                  </div>
                  <div className="flex-1 flex flex-col justify-center">
                      <p className="text-4xl font-black text-slate-900 tracking-tighter leading-none">1.2M</p>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2 flex items-center gap-1">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                        Trending ETB / Day
                      </p>
                  </div>
                  <div className="mt-8 space-y-3">
                     {[
                        { label: 'Sub Fees', val: '450k', color: 'bg-indigo-500' },
                        { label: 'Marketplace', val: '750k', color: 'bg-emerald-500' }
                     ].map((it, i) => (
                        <div key={i} className="flex items-center gap-3">
                           <div className={`w-1 h-3 rounded-full ${it.color}`}></div>
                           <span className="text-[11px] font-black text-slate-500 uppercase">{it.label}</span>
                           <span className="ml-auto text-xs font-black text-slate-700">{it.val}</span>
                        </div>
                     ))}
                  </div>
              </Card>
           </div>

           {/* RECENT FEED PREVIEW */}
           <Card>
              <div className="flex items-center justify-between mb-8">
                 <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
                    <Activity size={20} className="text-indigo-600" />
                    Global Signals
                 </h3>
                 <Link to="/admin/audit" className="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-primary transition-colors">See Master Logs</Link>
              </div>
              <ul className="space-y-4">
                {platformStats.activityFeed.slice(0, 4).map((item, index) => (
                  <li key={index} className="flex items-center gap-4 p-3 rounded-2xl bg-white border border-slate-100 hover:shadow-lg hover:shadow-slate-100 transition-all cursor-pointer group">
                    <div className={`p-2.5 rounded-xl shrink-0 ${
                      item.type === "New Creator" ? "bg-purple-100 text-purple-600" :
                      item.type === "New Subscription" ? "bg-blue-100 text-blue-600" :
                      "bg-emerald-100 text-emerald-600"
                    }`}>
                      {item.type === "New Creator" && <UserPlus size={18} />}
                      {item.type === "New Subscription" && <Gift size={18} />}
                      {item.type === "Payout" && <CheckCircle size={18} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-800 group-hover:text-primary transition-colors">{item.text}</p>
                      <p className="text-[10px] text-slate-400 font-black uppercase tracking-tighter mt-1">{item.time}</p>
                    </div>
                    <ChevronRight size={16} className="text-slate-200 group-hover:text-slate-400 transition-all group-hover:translate-x-1" />
                  </li>
                ))}
              </ul>
           </Card>
        </div>

      </div>
    </div>
  );
};

export default AdminDashboardPage;

